import { Injectable, OnDestroy, TemplateRef } from '@angular/core';
import { CmpsDialog, CmpsDialogRef } from '@cmps/ng-uikit';
import { Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';

import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { CallDialogComponent, CallDialogData } from '../call-dialog';
import { CallHudComponent } from '../call-hud/call-hud.component';

import { callHandleActive } from './function/call-handle-active.function';
import { CallHandle } from './model/call-handle.model';
import { CallStatusNotification } from './model/call-status-notification.model';
import { CallStatus } from './model/call-status.model';
import { HubMethods } from './model/hub-methods.model';
import { ImportTranscriptParams } from './model/import-transcript-params.model';
import { ListMeetingParams } from './model/list-meeting-params.model';
import { MeetingNotification } from './model/meeting-notification.model';
import { MeetingRequest } from './model/meeting-request.model';
import { SocketService } from './socket.service';

@Injectable({ providedIn: 'root' })
@TakeUntilDestroy()
export class CallStatusService implements OnDestroy {
    readonly message: Observable<{ hubMethod: string; payload: any }>;
    callHandle: CallHandle | null;

    /* TODO refactor */
    buttons: Array<TemplateRef<any>>;

    private readonly _messageSubj: Subject<{ hubMethod: string; payload: any }>;
    private _callDialogRef: CmpsDialogRef<CallDialogComponent, void>;
    private _callHudRef: CmpsDialogRef<CallHudComponent, void>;

    constructor(
        private readonly _socketService: SocketService,
        private readonly _dialog: CmpsDialog,
    ) {
        this._messageSubj = new Subject();
        this.message = this._messageSubj.asObservable();

        this._setupBindings();
    }

    ngOnDestroy(): void {
        this.hideCallDialog();
        this.hideCallHud();

        this._messageSubj?.complete();
    }

    showCallDialog(opts: MeetingRequest): void {
        this._callDialogRef?.close();

        const data: CallDialogData = {
            meetingRequest: opts,
        };

        this._callDialogRef = this._dialog.open(CallDialogComponent, {
            movable: true,
            data,
        });
    }

    hideCallDialog(): void {
        this._callDialogRef?.close();
    }

    showCallHud(): void {
        this._callHudRef?.close();

        this._callHudRef = this._dialog.open(CallHudComponent, {
            disableOverlayOverflowHidden: true,
            overlayClass: 'cmps-teamscall-call-hud--overlay-class',
            // portalClass: 'cmps-teamscall-call-hud--portal-class',
            portalWrapperClass: 'cmps-teamscall-call-hud--portal-wrapper-class',
            disableBackdrop: true,
            disableClose: true,
            // movable: true,
            placement: 'top-center',
        });
    }

    hideCallHud(): void {
        this._callHudRef?.close();
    }

    async retrieveCurrent(): Promise<void> {
        await this._socketService.send(HubMethods.RetrieveCurrentCall);
    }

    async createMeeting(request: MeetingRequest): Promise<void> {
        await this._socketService.send(HubMethods.CreateMeeting, request);
    }

    async listMeetings(params: ListMeetingParams): Promise<void> {
        await this._socketService.send(HubMethods.ListMeetings, params);
    }

    async inviteToCurrentCall(inviteUserId: number): Promise<void> {
        await this._socketService.send(
            HubMethods.InviteToCurrentCall,
            inviteUserId,
        );
    }

    async endCurrentCall(): Promise<void> {
        await this._socketService.send(HubMethods.EndCurrentCall);
    }

    async importTranscript(params: ImportTranscriptParams): Promise<void> {
        await this._socketService.send(HubMethods.ImportTranscript, params);
    }

    private _setupBindings(): void {
        if (this._socketService.created) {
            this._bind();
        }
        this._socketService.didCreate
            .pipe(
                tap((_socketService) => {
                    this._bind();
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        if (this._socketService.connected) {
            void this.retrieveCurrent();
        }
        this._socketService.didConnect
            .pipe(
                tap((_socketService) => {
                    void this.retrieveCurrent();
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _bind(): void {
        this._socketService.on(
            HubMethods.OnCurrentCall,
            (payload: CallHandle | null) => {
                payload?.stateHistory?.forEach((e) => {
                    e.timestamp = new Date(e.timestamp);
                });
                payload?.stateHistory?.sort(
                    (a, b) => +b.timestamp - +a.timestamp,
                );
                this.callHandle = payload;

                if (callHandleActive(payload)) {
                    this.showCallHud();
                } else {
                    this.hideCallHud();
                }

                this._messageSubj.next({
                    hubMethod: HubMethods.OnCurrentCall,
                    payload,
                });
            },
        );

        this._socketService.on(
            HubMethods.OnMeetingList,
            (payload: MeetingNotification) => {
                this._messageSubj.next({
                    hubMethod: HubMethods.OnMeetingList,
                    payload,
                });
            },
        );

        this._socketService.on(
            HubMethods.OnCallStatus,
            (payload: CallStatusNotification) => {
                switch (payload?.status) {
                    case CallStatus.MEETING_STARTED:
                        this.showCallHud();
                        break;
                    case CallStatus.MEETING_ENDED:
                    case CallStatus.ENDED:
                        this.hideCallDialog();
                        this.hideCallHud();
                        break;
                }

                this._messageSubj.next({
                    hubMethod: HubMethods.OnCallStatus,
                    payload,
                });
            },
        );
    }
}
